import { useEffect, useRef } from 'react';
import type { RecordResult, RecordStructure } from '../@types/records';
import { RecordSaveState } from '../components/record/record.component';
import { useGetFieldVisibilitiesMutation, useGetSectionVisibilitiesMutation } from '../redux/services/record';

interface RecordFieldVisibilityParams {
  recordSaveState: RecordSaveState;
  singleRecordData?: RecordStructure | undefined;
  defaultRecordData?: RecordStructure | undefined;
  defaultLinkedRecordData?: RecordStructure | undefined;
}

export function useRecordFieldVisibilities({
  recordSaveState,
  singleRecordData,
  defaultRecordData,
  defaultLinkedRecordData,
}: RecordFieldVisibilityParams) {
  const fieldVisibilitiesPromise = useRef<Promise<Record<number, boolean>> | undefined>(undefined);
  const sectionVisibilitiesPromise = useRef<Promise<Record<number, boolean>> | undefined>(undefined);

  const [getFieldVisibilities, { isLoading: isFieldVisibilitiesLoading }] = useGetFieldVisibilitiesMutation();
  const [getSectionVisibilities, { isLoading: isSectionVisibilitiesLoading }] = useGetSectionVisibilitiesMutation();

  useEffect(() => {
    let recordData: RecordResult | undefined = undefined;
    if (recordSaveState === RecordSaveState.Update && singleRecordData) {
      recordData = singleRecordData.original;
    } else if (recordSaveState === RecordSaveState.New && defaultRecordData) {
      recordData = defaultRecordData.original;
    } else if (recordSaveState === RecordSaveState.New && defaultLinkedRecordData) {
      recordData = defaultLinkedRecordData.original;
    }

    if (recordData) {
      if (recordData.requiresVisibilityCheckFields?.length) {
        fieldVisibilitiesPromise.current = getFieldVisibilities({
          fieldIds: recordData.requiresVisibilityCheckFields,
          record: recordData,
        }).unwrap();
      } else {
        fieldVisibilitiesPromise.current = Promise.resolve({});
      }

      if (recordData.requiresVisibilityCheckFieldGroups?.length) {
        sectionVisibilitiesPromise.current = getSectionVisibilities({
          sectionIds: recordData.requiresVisibilityCheckFieldGroups,
          record: recordData,
        }).unwrap();
      } else {
        sectionVisibilitiesPromise.current = Promise.resolve({});
      }
    }
  }, [
    singleRecordData,
    defaultRecordData,
    defaultLinkedRecordData,
    recordSaveState,
    getFieldVisibilities,
    getSectionVisibilities,
  ]);

  return {
    fieldVisibilitiesPromise: fieldVisibilitiesPromise.current,
    sectionVisibilitiesPromise: sectionVisibilitiesPromise.current,
    isLoading: isFieldVisibilitiesLoading || isSectionVisibilitiesLoading,
  };
}
